import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileText,
  CheckCircle,
  RefreshCw,
  Banknote,
  Building2,
  HardHat,
  User,
  Search,
  Link,
  AlertCircle,
  Plus,
  Upload,
  Ban,
  Wand2,
} from 'lucide-react';
import { GlassCard, GlassButton, GlassInput, GlassSelect, GlassTable, GlassModal, FadeIn } from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOKS = {
  imobiliaria: { name: 'Imobiliaria', color: '#22c55e', icon: Building2 },
  construtora: { name: 'Construtora', color: '#3b82f6', icon: HardHat },
  pessoal: { name: 'Pessoal', color: '#8b5cf6', icon: User },
  geral: { name: 'Geral', color: '#94a3b8', icon: FileText },
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const normalizeText = (value) => String(value || '').toLowerCase();

const INITIAL_ACCOUNT = {
  nome: '',
  banco: '',
  agencia: '',
  conta: '',
  tipo: 'corrente',
  saldo_atual: '0',
};

export const FinanceiroConciliacao = () => {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ status: 'all', search: '' });
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [accountForm, setAccountForm] = useState(INITIAL_ACCOUNT);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [statementFile, setStatementFile] = useState(null);
  const [statementLines, setStatementLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAccount, setSavingAccount] = useState(false);
  const [importing, setImporting] = useState(false);
  const [lineActionId, setLineActionId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadConciliacao = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/conciliacao');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar conciliacao.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConciliacao();
  }, [loadConciliacao]);

  const summary = data?.summary || {};
  const movimentacoes = data?.movimentacoes || [];
  const contas = data?.contas || [];

  const loadStatementLines = useCallback(async (contaId = selectedAccountId) => {
    try {
      const params = contaId ? { conta_id: contaId } : {};
      const response = await api.get('/contabilidade/conciliacao/extrato-linhas', { params });
      setStatementLines(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar extrato importado.');
    }
  }, [selectedAccountId]);

  const handleAccountChange = (field, value) => {
    setAccountForm((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    loadStatementLines();
  }, [loadStatementLines]);

  const handleCreateAccount = async () => {
    setSavingAccount(true);
    setError('');
    try {
      await api.post('/contabilidade/contas-bancarias', {
        ...accountForm,
        saldo_atual: Number(String(accountForm.saldo_atual).replace(',', '.')),
      });
      setIsAccountModalOpen(false);
      setAccountForm(INITIAL_ACCOUNT);
      await loadConciliacao();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar conta bancaria.');
    } finally {
      setSavingAccount(false);
    }
  };

  const handleImportStatement = async () => {
    if (!selectedAccountId || !statementFile) {
      setError('Selecione uma conta bancaria e um arquivo CSV/OFX.');
      return;
    }
    setImporting(true);
    setError('');
    setSuccess('');
    try {
      const formData = new FormData();
      formData.append('conta_id', selectedAccountId);
      formData.append('arquivo', statementFile);
      const response = await api.post('/contabilidade/conciliacao/importacoes', formData);
      setStatementFile(null);
      setStatementLines(response.data.linhas || []);
      setSuccess(response.data.duplicado
        ? response.data.message
        : `${response.data.message || 'Extrato importado.'} Confira as sugestões antes de confirmar qualquer conciliação.`);
      await loadConciliacao();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar extrato.');
    } finally {
      setImporting(false);
    }
  };

  const handleConfirmMatch = async (matchId) => {
    setLineActionId(matchId);
    setError('');
    try {
      await api.post(`/contabilidade/conciliacao/matches/${matchId}/confirmar`);
      await loadStatementLines();
      await loadConciliacao();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao confirmar conciliacao.');
    } finally {
      setLineActionId('');
    }
  };

  const handleIgnoreLine = async (lineId) => {
    setLineActionId(lineId);
    setError('');
    try {
      await api.post(`/contabilidade/conciliacao/extrato-linhas/${lineId}/ignorar`);
      await loadStatementLines();
      await loadConciliacao();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao ignorar linha.');
    } finally {
      setLineActionId('');
    }
  };

  const handleCreateMovementFromLine = async (lineId) => {
    setLineActionId(lineId);
    setError('');
    try {
      await api.post(`/contabilidade/conciliacao/extrato-linhas/${lineId}/criar-movimentacao`, {});
      await loadStatementLines();
      await loadConciliacao();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar movimentacao pelo extrato.');
    } finally {
      setLineActionId('');
    }
  };

  const filteredData = useMemo(() => {
    return movimentacoes.filter((item) => {
      if (filters.status !== 'all' && item.conciliation_status !== filters.status) return false;
      if (filters.search) {
        const haystack = [
          item.descricao,
          item.pessoa_nome,
          item.numero_documento,
          item.conta,
          item.categoria,
        ].map(normalizeText).join(' ');
        if (!haystack.includes(normalizeText(filters.search))) return false;
      }
      return true;
    });
  }, [filters, movimentacoes]);

  const columns = [
    {
      key: 'data_vencimento',
      title: 'Data',
      accessor: 'data_vencimento',
      render: (row) => formatDate(row.data_pagamento || row.data_vencimento),
    },
    {
      key: 'descricao',
      title: 'Descricao',
      accessor: 'descricao',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.descricao || row.pessoa_nome || 'Lancamento sem descricao'}</p>
          <p className="text-xs text-white/40">{row.conta || row.numero_documento || '-'}</p>
        </div>
      ),
    },
    {
      key: 'valor',
      title: 'Valor',
      accessor: 'valor_assinado',
      render: (row) => (
        <span className={row.valor_assinado >= 0 ? 'text-green-400' : 'text-red-400'}>
          {formatCurrency(row.valor_assinado)}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      accessor: 'conciliation_status',
      render: (row) => row.matched ? (
        <span className="flex items-center gap-1 text-green-400 text-sm">
          <CheckCircle className="w-4 h-4" />
          Conciliado
        </span>
      ) : (
        <span className="flex items-center gap-1 text-yellow-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          Pendente
        </span>
      ),
    },
    {
      key: 'book',
      title: 'Book',
      accessor: 'book',
      render: (row) => {
        const book = BOOKS[row.book] || BOOKS.geral;
        return (
          <div className="flex items-center gap-2">
            {React.createElement(book.icon, {
              className: 'w-4 h-4',
              style: { color: book.color },
            })}
            <span style={{ color: book.color }}>{book.name}</span>
          </div>
        );
      },
    },
  ];

  const statementColumns = [
    {
      key: 'data',
      title: 'Data',
      accessor: 'data',
      render: (row) => formatDate(row.data),
    },
    {
      key: 'descricao',
      title: 'Extrato',
      accessor: 'descricao',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.descricao || 'Linha sem descricao'}</p>
          <p className="text-xs text-white/40">{row.documento || row.conta || '-'}</p>
        </div>
      ),
    },
    {
      key: 'valor',
      title: 'Valor',
      accessor: 'valor',
      render: (row) => (
        <span className={row.valor >= 0 ? 'text-green-400' : 'text-red-400'}>
          {formatCurrency(row.valor)}
        </span>
      ),
    },
    {
      key: 'sugestao',
      title: 'Sugestao',
      accessor: 'matches',
      render: (row) => {
        const bestMatch = (row.matches || []).find((match) => match.status === 'sugerido');
        if (!bestMatch?.movimentacao) {
          return <span className="text-white/40">Sem sugestao</span>;
        }
        return (
          <div>
            <p className="text-white/90">{bestMatch.movimentacao.descricao || bestMatch.movimentacao.pessoa_nome || 'Movimentacao'}</p>
            <p className="text-xs text-white/40">{bestMatch.score}% · {formatCurrency(bestMatch.movimentacao.valor)}</p>
          </div>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      accessor: 'status',
      render: (row) => (
        <span className={row.status === 'conciliado' ? 'text-green-400' : row.status === 'ignorado' ? 'text-white/40' : 'text-yellow-400'}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'acoes',
      title: '',
      accessor: 'id',
      render: (row) => {
        if (row.status !== 'pendente') return null;
        const bestMatch = (row.matches || []).find((match) => match.status === 'sugerido');
        return (
          <div className="flex items-center gap-2 justify-end">
            {bestMatch && (
              <GlassButton
                size="sm"
                icon={CheckCircle}
                loading={lineActionId === bestMatch.id}
                onClick={() => handleConfirmMatch(bestMatch.id)}
              >
                Confirmar
              </GlassButton>
            )}
            <GlassButton
              size="sm"
              variant="secondary"
              icon={Wand2}
              loading={lineActionId === row.id}
              onClick={() => handleCreateMovementFromLine(row.id)}
            >
              Criar
            </GlassButton>
            <GlassButton
              size="sm"
              variant="ghost"
              icon={Ban}
              loading={lineActionId === row.id}
              onClick={() => handleIgnoreLine(row.id)}
            >
              Ignorar
            </GlassButton>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Conciliacao Bancaria</h1>
          <p className="text-white/50">
            {loading ? 'Carregando conciliacao real...' : `${summary.total_movimentacoes || 0} lancamentos analisados`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadConciliacao} loading={loading}>
            Atualizar
          </GlassButton>
          <GlassButton icon={Plus} onClick={() => setIsAccountModalOpen(true)}>
            Nova conta
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}
      {success && (
        <GlassCard className="p-4 border-emerald-500/30 bg-emerald-500/10" hover={false}>
          <p className="text-sm text-emerald-200">{success}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Taxa conciliacao</p>
              <p className="text-2xl font-bold text-green-400">{Number(summary.taxa_conciliacao || 0).toFixed(0)}%</p>
            </div>
            <div className="p-3 rounded-xl bg-green-500/20">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Conciliados</p>
              <p className="text-2xl font-bold text-accent-primary">{summary.conciliadas || 0}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Link className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Pendentes</p>
              <p className="text-2xl font-bold text-yellow-400">{summary.pendentes || 0}</p>
            </div>
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <AlertCircle className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Contas bancarias</p>
              <p className="text-2xl font-bold text-white/90">{summary.contas_bancarias || 0}</p>
            </div>
            <div className="p-3 rounded-xl bg-white/5">
              <Banknote className="w-6 h-6 text-white/60" />
            </div>
          </div>
        </GlassCard>
      </div>

      <FadeIn>
        <GlassCard>
          <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-lg font-semibold text-white/90">Importacao de Extrato</h3>
              <p className="text-sm text-white/50">
                {summary.extrato_total || 0} linhas importadas · {summary.extrato_pendente || 0} pendentes
              </p>
            </div>
            <GlassButton icon={Upload} onClick={handleImportStatement} loading={importing}>
              Importar
            </GlassButton>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-[minmax(220px,1fr)_minmax(260px,1.2fr)] gap-4">
            <GlassSelect
              label="Conta bancaria"
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
              placeholder="Selecione a conta"
              options={contas.map((conta) => ({
                value: conta.id,
                label: `${conta.nome}${conta.banco ? ` · ${conta.banco}` : ''}`,
              }))}
            />
            <GlassInput
              label="Arquivo CSV/OFX"
              type="file"
              accept=".csv,.ofx,.qfx,.pdf"
              onChange={(e) => setStatementFile(e.target.files?.[0] || null)}
            />
          </div>
          <GlassTable
            columns={statementColumns}
            data={statementLines}
            searchable={false}
            emptyMessage="Nenhuma linha de extrato importada"
          />
        </GlassCard>
      </FadeIn>

      <FadeIn>
        <GlassCard>
          <div className="p-4 border-b border-white/10 flex items-center gap-4 flex-wrap">
            <GlassInput
              icon={Search}
              placeholder="Buscar lancamento..."
              className="max-w-sm"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
            <GlassSelect
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder=""
              className="min-w-[180px]"
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'conciliado', label: 'Conciliados' },
                { value: 'pendente', label: 'Pendentes' },
              ]}
            />
          </div>
          <GlassTable
            columns={columns}
            data={filteredData}
            searchable={false}
            emptyMessage={loading ? 'Carregando lancamentos...' : 'Nenhum lancamento financeiro para conciliar'}
          />
        </GlassCard>
      </FadeIn>

      <FadeIn delay={0.2}>
        <GlassCard>
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white/90">Resumo por Conta</h3>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {contas.length > 0 ? contas.map((conta) => (
              <div key={conta.id} className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
                <p className="font-medium text-white/90">{conta.nome}</p>
                <p className="text-sm text-white/50">{conta.banco || 'Banco nao informado'}</p>
                <div className="mt-4 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-white/50">Saldo</span>
                    <span className="text-white/90">{formatCurrency(conta.saldo_atual)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Movimentacoes</span>
                    <span className="text-white/90">{conta.movimentacoes}</span>
                  </div>
                </div>
              </div>
            )) : (
              <div className="md:col-span-3 text-center text-white/50 py-8">
                Nenhuma conta bancaria cadastrada.
              </div>
            )}
          </div>
        </GlassCard>
      </FadeIn>

      <GlassModal
        isOpen={isAccountModalOpen}
        onClose={() => setIsAccountModalOpen(false)}
        title="Nova Conta Bancaria"
        icon={Banknote}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsAccountModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleCreateAccount} loading={savingAccount}>
              Salvar
            </GlassButton>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput
            label="Nome"
            value={accountForm.nome}
            onChange={(e) => handleAccountChange('nome', e.target.value)}
            placeholder="Ex.: Conta Corrente MMI"
          />
          <GlassInput
            label="Banco"
            value={accountForm.banco}
            onChange={(e) => handleAccountChange('banco', e.target.value)}
            placeholder="Banco"
          />
          <GlassInput
            label="Agencia"
            value={accountForm.agencia}
            onChange={(e) => handleAccountChange('agencia', e.target.value)}
            placeholder="0000"
          />
          <GlassInput
            label="Conta"
            value={accountForm.conta}
            onChange={(e) => handleAccountChange('conta', e.target.value)}
            placeholder="00000-0"
          />
          <GlassSelect
            label="Tipo"
            value={accountForm.tipo}
            onChange={(e) => handleAccountChange('tipo', e.target.value)}
            placeholder=""
            options={[
              { value: 'corrente', label: 'Corrente' },
              { value: 'poupanca', label: 'Poupanca' },
              { value: 'investimento', label: 'Investimento' },
            ]}
          />
          <GlassInput
            label="Saldo inicial"
            type="number"
            step="0.01"
            value={accountForm.saldo_atual}
            onChange={(e) => handleAccountChange('saldo_atual', e.target.value)}
            placeholder="0,00"
          />
        </div>
      </GlassModal>
    </div>
  );
};

export default FinanceiroConciliacao;
